import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Paper,
  Box,
  Fade,
  Slide
} from "@mui/material";
import {
  Cake as CakeIcon,
  Send as SendIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Close as CloseIcon
} from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";


const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const BirthdayModal = ({ open, onClose, user }) => {
  const [birthdayData, setBirthdayData] = useState({ users: [], contacts: [], settings: null });
  const [loading, setLoading] = useState(false);
  const [sendingMessages, setSendingMessages] = useState(new Set());

  useEffect(() => {
    if (open) {
      fetchTodayBirthdays();
    }
  }, [open]);

  const fetchTodayBirthdays = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/birthdays/today");
      setBirthdayData(data.data);
    } catch (error) {
      toast.error("Error al cargar los cumpleañeros");
    } finally {
      setLoading(false);
    }
  };

  const handleSendBirthdayMessage = async (contactId, contactName) => {
    setSendingMessages(prev => new Set([...prev, contactId]));
    
    try {
      await api.post("/birthdays/send-message", { contactId });
      toast.success(`¡Mensaje de felicitación enviado a ${contactName}! 🎉`);
      
      // Atualizar o estado local para marcar como enviada
      setBirthdayData(prev => ({
        ...prev,
        contacts: prev.contacts.map(contact => 
          contact.id === contactId 
            ? { ...contact, messageSent: true }
            : contact
        )
      }));
    } catch (error) {
      // Verificar se é erro de mensagem já enviada
      if (error.response?.status === 409) {
        toast.warning("El mensaje de cumpleaños ya fue enviado hoy a este contacto");
        
        // Atualizar o estado local para marcar como enviada
        setBirthdayData(prev => ({
          ...prev,
          contacts: prev.contacts.map(contact => 
            contact.id === contactId 
              ? { ...contact, messageSent: true }
              : contact
          )
        }));
      } else {
        toast.error("Error al enviar el mensaje de cumpleaños");
      }
    } finally {
      setSendingMessages(prev => {
        const newSet = new Set(prev);
        newSet.delete(contactId);
        return newSet;
      });
    }
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatAge = (age) => {
    if (!age) return "N/A";
    return `${age} ano${age !== 1 ? 's' : ''}`;
  };

  const hasBirthdays = birthdayData.users.length > 0 || birthdayData.contacts.length > 0;

  if (!hasBirthdays && !loading) {
    return null; // Não mostrar modal se não há aniversariantes
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      sx={{ "& .MuiDialog-paper":{ borderRadius: "16px", maxWidth:600, width:"90%", overflow:"hidden" } }}
      TransitionComponent={Transition}
      maxWidth="md"
      fullWidth
    >
      {/* Header festivo */}
      <Box sx={{ background:"linear-gradient(135deg, #ff6b6b 0%, #feca57 50%, #48dbfb 100%)", p:"32px 24px", textAlign:"center", position:"relative", overflow:"hidden", "@keyframes rotate":{ from:{ transform:"rotate(0deg)" }, to:{ transform:"rotate(360deg)" } }, "@keyframes birthdayPulse":{ "0%,100%":{ transform:"scale(1)" }, "50%":{ transform:"scale(1.05)" } } }}>
        <IconButton sx={(theme) => ({ position:"absolute", top:theme.spacing(1), right:theme.spacing(1), color:"rgba(255,255,255,0.8)", zIndex:2, "&:hover":{ backgroundColor:"rgba(255,255,255,0.1)" } })} onClick={onClose}>
          <CloseIcon />
        </IconButton>
        
        <CakeIcon sx={{ fontSize:48, mb:2, animation:"rotate 2s linear infinite" }} />
        <Typography variant="h4" sx={{ color:"white", fontWeight:700, fontSize:"1.8rem", textShadow:"0 2px 4px rgba(0,0,0,0.2)", mb:1, position:"relative", zIndex:1 }}>
          🎉 ¡Felicidades!
        </Typography>
        <Typography sx={{ color:"rgba(255,255,255,0.9)", fontSize:"1rem", position:"relative", zIndex:1 }}>
          ¡Hoy es un día especial!
        </Typography>
      </Box>

      {/* Content */}
      <DialogContent sx={{ p:3, maxHeight:"60vh", overflowY:"auto" }}>
        {loading ? (
          <Box sx={(theme) => ({ textAlign:"center", p:4, color:theme.palette.text.secondary })}>
            <Typography>Cargando cumpleañeros...</Typography>
          </Box>
        ) : (
          <List sx={{ width:"100%" }}>
            {/* Aniversariantes Usuários */}
            {birthdayData.users.map((birthdayUser) => (
              <ListItem 
                key={`user-${birthdayUser.id}`}
                sx={(theme) => ({ mb:2, backgroundColor:"#f8f9ff", border:`1px solid ${theme.palette.divider}`, borderRadius:"12px", borderLeft:`4px solid ${theme.palette.secondary.main}`, transition:"all 0.3s ease", "&:hover":{ transform:"translateX(4px)", boxShadow:4 } })}
              >
                <ListItemAvatar>
                  <Avatar sx={{ width:56, height:56, background:"linear-gradient(135deg, #ff6b6b 0%, #feca57 100%)", animation:"birthdayPulse 2s infinite", position:"relative" }}>
                    {getInitials(birthdayUser.name)}
                  </Avatar>
                </ListItemAvatar>
                
                <ListItemText
                  primary={
                    <Typography sx={(theme) => ({ fontSize:"1.1rem", fontWeight:600, color:theme.palette.text.primary, mb:"4px" })}>
                      {birthdayUser.name}
                      {birthdayUser.id === user?.id && " (¡Eres tú!)"}
                    </Typography>
                  }
                  secondary={
                    <Box sx={(theme) => ({ display:"flex", alignItems:"center", gap:"4px", color:theme.palette.text.secondary, fontSize:"0.875rem" })}>
                      <PersonIcon fontSize="small" />
                      <span>Usuario del Sistema</span>
                    </Box>
                  }
                />
                
                <ListItemSecondaryAction>
                  <Chip
                    icon={<CakeIcon />}
                    label={formatAge(birthdayUser.age)}
                    sx={(theme) => ({ backgroundColor:theme.palette.primary.main, color:"white", fontWeight:"bold", fontSize:"0.875rem", "& .MuiChip-icon":{ color:"white" } })}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            ))}

            {/* Aniversariantes Contatos */}
            {birthdayData.contacts.map((birthdayContact) => (
              <ListItem 
                key={`contact-${birthdayContact.id}`}
                sx={(theme) => ({ mb:2, backgroundColor:theme.palette.background.paper, border:`1px solid ${theme.palette.divider}`, borderRadius:"12px", borderLeft:`4px solid ${theme.palette.primary.main}`, transition:"all 0.3s ease", "&:hover":{ transform:"translateX(4px)", boxShadow:4 } })}
              >
                <ListItemAvatar>
                  <Avatar sx={{ width:56, height:56, background:"linear-gradient(135deg, #667eea 0%, #764ba2 100%)", position:"relative" }}>
                    {getInitials(birthdayContact.name)}
                  </Avatar>
                </ListItemAvatar>
                
                <ListItemText
                  primary={
                    <Typography sx={(theme) => ({ fontSize:"1.1rem", fontWeight:600, color:theme.palette.text.primary, mb:"4px" })}>
                      {birthdayContact.name}
                    </Typography>
                  }
                  secondary={
                    <Box sx={(theme) => ({ display:"flex", alignItems:"center", gap:"4px", color:theme.palette.text.secondary, fontSize:"0.875rem" })}>
                      <PhoneIcon fontSize="small" />
                      <span>Contacto</span>
                      <Chip
                        icon={<CakeIcon />}
                        label={formatAge(birthdayContact.age)}
                        size="small"
                        style={{ marginLeft: 8 }}
                      />
                    </Box>
                  }
                />
                
                <ListItemSecondaryAction>
                  <Button
                    variant="contained"
                    sx={{ background:"linear-gradient(135deg, #10b981 0%, #059669 100%)", color:"white", minWidth:120, fontWeight:600, textTransform:"none", borderRadius:"8px", transition:"all 0.3s ease", "&:hover":{ transform:"translateY(-1px)", boxShadow:"0 4px 12px rgba(16,185,129,0.3)" }, "&:disabled":{ background:"#9ca3af", color:"white" } }}
                    startIcon={<SendIcon />}
                    onClick={() => handleSendBirthdayMessage(
                      birthdayContact.id, 
                      birthdayContact.name
                    )}
                    disabled={
                      sendingMessages.has(birthdayContact.id) || 
                      birthdayContact.messageSent
                    }
                  >
                    {sendingMessages.has(birthdayContact.id) 
                      ? "Enviando..."
                      : birthdayContact.messageSent
                      ? "Mensaje ya enviado"
                      : "Enviar Felicitación"
                    }
                  </Button>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={(theme) => ({ p:"16px 24px", borderTop:`1px solid ${theme.palette.divider}`, justifyContent:"space-between" })}>
        <Button 
          onClick={onClose} 
          color="inherit"
          sx={{ textTransform:"none", fontWeight:600, borderRadius:"8px", minWidth:120 }}
          variant="outlined"
        >
          Recordarme más tarde
        </Button>
        <Button
          onClick={onClose}
          color="primary"
          sx={{ textTransform:"none", fontWeight:600, borderRadius:"8px", minWidth:120 }}
          variant="contained"
        >
          ¡Entendido, gracias!
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BirthdayModal;